import { Fragment, useCallback, useEffect, useState } from "react";
import { api } from "../../api/client";
import { StatusBadge } from "../../components/Badge";
import { Dialog } from "../../components/Dialog";
import { useQuoteStatusLabels } from "../../api/quoteStatusLabels";
import { formatMoney } from "../../lib/money";
import type { InvoiceRequest, QuoteFlowStep, QuoteMoney, QuoteStatus } from "../../api/types";

// 報價單上方的財務摘要 + 流程條(業主 2026-09-30,財務整理 1、3)。
//
// **算數全在後端**(`GET /api/quotes/{id}/finance-summary`):這裡只照著印。
// 收款或雜支一改,那兩塊會丟一個 `quote-money-changed`,這一條就重讀一次。
//
// 為什麼不用 react-query:編輯器那棵樹的測試沒有 `QueryClientProvider`,
// 與 `ProjectCost`、`AttachmentPanel` 同一個慣例。

/** 收款或雜支改了之後丟這個事件,摘要會重讀。 */
export const MONEY_CHANGED = "quote-money-changed";

export function announceMoneyChanged(): void {
  window.dispatchEvent(new Event(MONEY_CHANGED));
}

// 流程條**只用狀態本身的名字**(業主 2026-09-30:「統一一下 好亂的狀態」)——
// 字樣與狀態徽章、「改狀態」的下拉讀同一份(後端給的),亮的那一格就是目前的狀態。
// 錢的進度(已開票、已收)不是狀態,看底下那排數字。
const STEPS: QuoteStatus[] = ["DRAFT", "SENT", "CLOSED"];

/** 2026-10-15 → 10/15。流程條每一格底下那個小日期。 */
function shortDate(iso: string | null): string {
  if (!iso) return "";
  const [, m, d] = iso.split("-");
  return `${Number(m)}/${Number(d)}`;
}

function Figure({
  label,
  value,
  href,
  note,
  tone,
  onClick,
}: {
  label: string;
  value: string;
  href?: string;
  note?: string;
  tone?: "danger";
  onClick?: () => void;
}) {
  const body = (
    <>
      <span className="block text-xs text-[var(--text-3)]">{label}</span>
      <span
        className={
          "block tabular-nums text-sm font-semibold " +
          (tone === "danger" ? "text-[var(--danger)]" : "text-[var(--text)]")
        }
      >
        {value}
      </span>
      {note ? <span className="block text-xs text-[var(--danger)]">{note}</span> : null}
    </>
  );
  const cls = "min-w-[7.5rem] rounded-[var(--radius)] px-2 py-1";
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={cls + " text-left hover:bg-[var(--surface-2)]"}>
        {body}
      </button>
    );
  }
  return href ? (
    <a href={href} className={cls + " no-underline hover:bg-[var(--surface-2)]"}>
      {body}
    </a>
  ) : (
    <div className={cls}>{body}</div>
  );
}

export interface MoneyStripProps {
  quoteId: number;
  status: QuoteStatus;
}

export function MoneyStrip({ quoteId, status }: MoneyStripProps) {
  const [m, setM] = useState<QuoteMoney | null>(null);
  const labels = useQuoteStatusLabels();

  const refresh = useCallback(() => {
    // 讀不到不該讓整個編輯器壞掉 —— 吞掉例外,而且檢查形狀(理由同 `ProjectCost`)。
    api
      .get<QuoteMoney>(`/quotes/${quoteId}/finance-summary`)
      .then((got) => setM(got && typeof got === "object" && "deal_amount" in got ? got : null))
      .catch(() => undefined);
  }, [quoteId]);

  // 狀態改了也重讀:流程條的那一格與日期跟著狀態走
  useEffect(() => {
    refresh();
    window.addEventListener(MONEY_CHANGED, refresh);
    return () => window.removeEventListener(MONEY_CHANGED, refresh);
  }, [refresh, status]);

  const [invoicesOpen, setInvoicesOpen] = useState(false);
  const stopped = m?.flow ? m.flow.stopped : status === "CANCELLED";
  // 後端給了流程(五格、含日期)就照它畫;還沒拿到時先用狀態畫前三格
  const fallbackAt = STEPS.indexOf(status);
  const steps: (QuoteFlowStep & { current: boolean })[] = m?.flow
    ? m.flow.steps.map((step) => ({ ...step, current: step.key === m.flow?.current }))
    : STEPS.map((key, i) => ({
        key, label: labels?.[key] || key, date: null,
        done: !stopped && i <= fallbackAt, current: !stopped && i === fallbackAt,
      }));
  const losing = m ? m.profit.startsWith("-") : false;

  return (
    <section
      aria-label="財務摘要"
      className="flex flex-col gap-2 rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)] px-3 py-2"
    >
      <div className="flex flex-wrap items-center gap-2">
        <ol aria-label="流程" className="m-0 flex list-none flex-wrap items-start gap-1 p-0 text-xs">
          {steps.map((step, i) => (
            <Fragment key={step.key}>
              {i > 0 ? <li aria-hidden="true" className="pt-0.5 text-[var(--text-3)]">›</li> : null}
              <li
                data-done={String(step.done)}
                aria-current={step.current ? "step" : undefined}
                className="flex flex-col items-center gap-0.5"
              >
                <span
                  className={
                    "rounded-full border px-2 py-0.5 " +
                    (stopped
                      ? "border-[var(--border)] text-[var(--text-3)]"
                      : step.current
                        ? "border-[var(--accent)] bg-[var(--accent-soft)] font-semibold text-[var(--accent-hover)]"
                        : step.done
                          ? "border-[var(--ok)] text-[var(--ok)]"
                          : "border-[var(--border)] text-[var(--text-3)]")
                  }
                >
                  {step.label}
                </span>
                {/* 每一格底下的小日期(業主 2026-10-15);還沒走到的不寫 */}
                {step.date ? <span className="tabular-nums text-[10px] text-[var(--text-3)]">{shortDate(step.date)}</span> : null}
                {/* 已開票:發票上的日期跟開進系統的那天不一樣(補登過去日期的發票)時,兩個都寫 */}
                {step.invoice_date ? (
                  <span className="tabular-nums text-[10px] text-[var(--text-3)]">發票日 {shortDate(step.invoice_date)}</span>
                ) : null}
              </li>
            </Fragment>
          ))}
        </ol>
        {stopped ? <StatusBadge status="CANCELLED" /> : null}
      </div>

      {m ? (
        <div className="flex flex-wrap gap-1">
          <Figure label="成交金額" value={m.deal ? formatMoney(m.deal_amount) : "還沒結案"} />
          {/* 點了看這張單的開票申請與發票(業主 2026-10-15);財務還沒開的每一列寫「申請中」 */}
          <Figure
            label="已開票"
            value={m.invoice_count > 0 ? `${m.invoice_count} 張 · ${formatMoney(m.invoiced_amount)}` : "還沒開"}
            onClick={() => setInvoicesOpen(true)}
          />
          <Figure label="已收" value={formatMoney(m.received)} href="#quote-receipts" />
          <Figure
            label="未收"
            value={formatMoney(m.outstanding)}
            href="#quote-receipts"
            note={m.oldest_overdue_days !== null ? `已欠 ${m.oldest_overdue_days} 天` : undefined}
          />
          <Figure
            label="雜支"
            value={`${m.expense_count} 筆 · ${formatMoney(m.expense_amount)}`}
            href="#quote-cost"
          />
          <Figure
            label="毛利(依成交)"
            value={m.deal ? formatMoney(m.profit) : "—"}
            tone={losing ? "danger" : undefined}
          />
        </div>
      ) : null}
      <QuoteInvoicesDialog quoteId={quoteId} open={invoicesOpen} onClose={() => setInvoicesOpen(false)} />
    </section>
  );
}

/**
 * 這張單的開票申請與發票(報價單上「已開票」點開的那一個,業主 2026-10-15)。
 * 每一筆申請一列;財務開了的列出發票號碼、日期、金額,一筆申請開了幾張就列幾張;
 * 還沒開的寫「申請中」。
 */
export function QuoteInvoicesDialog({ quoteId, open, onClose }: { quoteId: number; open: boolean; onClose: () => void }) {
  const [rows, setRows] = useState<InvoiceRequest[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setFailed(false);
    api.get<InvoiceRequest[]>(`/quotes/${quoteId}/invoice-requests`)
      .then((got) => {
        if (alive) setRows(Array.isArray(got) ? got : []);
      })
      .catch(() => {
        if (alive) setFailed(true);
      });
    return () => {
      alive = false;
    };
  }, [open, quoteId]);

  return (
    <Dialog open={open} onClose={onClose} title="這張單的發票" maxWidth="max-w-2xl">
      {failed ? <p role="alert" className="m-0 text-sm text-[var(--danger)]">讀不到開票資料,請稍後再試。</p> : null}
      {rows !== null && rows.length === 0 ? <p className="m-0 text-sm text-[var(--text-3)]">這張單還沒有開票申請。</p> : null}
      {rows !== null && rows.length > 0 ? <RequestInvoiceTable rows={rows} /> : null}
    </Dialog>
  );
}

/** 開票申請 + 底下的發票,一張表(懸浮視窗與開票申請清單共用)。 */
export function RequestInvoiceTable({ rows }: { rows: InvoiceRequest[] }) {
  return (
    <div className="w-full overflow-x-auto">
      <table aria-label="開票資訊" className="w-full border-collapse text-left text-sm">
        <thead>
          <tr className="text-xs text-[var(--text-2)]">
            <th scope="col" className="px-2 py-1">申請</th>
            <th scope="col" className="px-2 py-1">發票號碼</th>
            <th scope="col" className="px-2 py-1">發票日期</th>
            <th scope="col" className="px-2 py-1 text-right">金額</th>
            <th scope="col" className="px-2 py-1">狀態</th>
          </tr>
        </thead>
        <tbody>
          {rows.flatMap((req) => {
            const label = req.payment_term_name
              ? `第 ${req.payment_term_seq} 期 ${req.payment_term_name}`
              : `申請 ${formatMoney(req.amount)}`;
            const invoices = req.invoices ?? [];
            if (invoices.length === 0) {
              return [
                <tr key={`r${req.id}`} className="border-t border-[var(--border)]">
                  <td className="px-2 py-1">{label}</td>
                  <td className="px-2 py-1 text-[var(--text-3)]">—</td>
                  <td className="px-2 py-1 text-[var(--text-3)]">—</td>
                  <td className="px-2 py-1 text-right tabular-nums">{formatMoney(req.amount)}</td>
                  <td className="px-2 py-1 text-[var(--warn)]">{req.status === "PENDING" ? "申請中" : req.status_label}</td>
                </tr>,
              ];
            }
            return invoices.map((inv, i) => (
              <tr key={`i${inv.id}`} className="border-t border-[var(--border)]">
                <td className="px-2 py-1">{i === 0 ? label : ""}</td>
                <td className="px-2 py-1 tabular-nums">{inv.invoice_no || "—"}</td>
                <td className="px-2 py-1 tabular-nums">{inv.invoice_date ?? "—"}</td>
                <td className="px-2 py-1 text-right tabular-nums">{formatMoney(inv.amount)}</td>
                <td className="px-2 py-1">{inv.status_label}</td>
              </tr>
            ));
          })}
        </tbody>
      </table>
    </div>
  );
}
